<template>
  <div
    v-if="isVisible && policyDocument"
    class="legal-modal-overlay"
    @click="closeModal"
  >
    <div
      class="legal-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @click.stop
    >
      <div class="legal-modal-header">
        <div>
          <h3 :id="titleId" class="legal-modal-title">{{ policyDocument.title }}</h3>
          <p v-if="policyDocument.subtitle" class="legal-modal-subtitle">
            {{ policyDocument.subtitle }}
          </p>
        </div>
        <button
          type="button"
          class="legal-close-btn"
          aria-label="Close"
          @click="closeModal"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="legal-modal-body">
        <template v-for="(block, index) in policyDocument.blocks" :key="index">
          <h4 v-if="block.type === 'heading'" class="legal-heading">
            {{ block.text }}
          </h4>
          <p v-else-if="block.type === 'paragraph'" class="legal-paragraph">
            {{ block.text }}
          </p>
          <ul v-else-if="block.type === 'list'" class="legal-list">
            <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
              {{ item }}
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import policiesText from "@/assets/policies.txt";
import { parsePolicies } from "@/utils/parsePolicies";

const policies = parsePolicies(policiesText);

export default {
  name: "LegalModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    documentType: {
      type: String,
      default: "terms",
    },
  },
  emits: ["close"],
  computed: {
    titleId() {
      return `legal-modal-title-${this.documentType}`;
    },
    policyDocument() {
      return policies[this.documentType] || policies.terms;
    },
  },
  watch: {
    isVisible(visible) {
      document.body.style.overflow = visible ? "hidden" : "";
      if (visible) {
        window.addEventListener("keydown", this.handleKeydown);
      } else {
        window.removeEventListener("keydown", this.handleKeydown);
      }
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    window.removeEventListener("keydown", this.handleKeydown);
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeModal();
      }
    },
  },
};
</script>

<style scoped>
.legal-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10050;
  padding: 1.25rem;
  backdrop-filter: blur(4px);
}

.legal-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 760px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.legal-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.75rem 1.1rem;
  border-bottom: 1px solid #eee3d6;
  background: #faf6f1;
}

.legal-modal-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #2c241c;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.legal-modal-subtitle {
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  color: #8a6a4a;
  font-style: italic;
}

.legal-close-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.4rem 0.55rem;
  border-radius: 50%;
  line-height: 1;
}

.legal-close-btn:hover {
  background: rgba(180, 100, 40, 0.12);
  color: #b46428;
}

.legal-modal-body {
  padding: 1.1rem 1.75rem 1.75rem;
  overflow-y: auto;
  color: #3d342c;
}

.legal-heading {
  margin: 1.15rem 0 0.55rem;
  font-size: 1.02rem;
  font-weight: 600;
  color: #b46428;
}

.legal-heading:first-child {
  margin-top: 0.25rem;
}

.legal-paragraph {
  margin: 0 0 0.75rem;
  line-height: 1.65;
  font-size: 0.95rem;
}

.legal-list {
  margin: 0.15rem 0 0.9rem;
  padding-left: 1.2rem;
}

.legal-list li {
  margin-bottom: 0.4rem;
  line-height: 1.6;
  font-size: 0.95rem;
}

@media (max-width: 576px) {
  .legal-modal {
    max-height: 92vh;
    border-radius: 12px;
  }

  .legal-modal-header,
  .legal-modal-body {
    padding-left: 1.15rem;
    padding-right: 1.15rem;
  }

  .legal-modal-title {
    font-size: 1.1rem;
  }
}
</style>
